import React, { useState, useEffect, useRef } from "react";
import { Plus, X, Loader2, ChevronDown, ChevronRight, Building2, MapPin } from "lucide-react";
import { fetchChildOffices, updateUserOffice } from "../api";
import Cookies from "js-cookie";

const OfficeDropdown = ({ userEmail, userOrganizations = [], officeCode, onOfficeChange }) => {
  const [allOffices, setAllOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const token = Cookies.get("authToken");

  const userOfficeCodes = userOrganizations.map((org) => org.officeCode);

  // Fetch all offices when dropdown is opened
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchChildOffices(officeCode, token)
        .then((res) => setAllOffices(res.data || []))
        .finally(() => setLoading(false));
    }
  }, [open, officeCode, token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckbox = async (code, checked) => {
    try {
      setLoading(true);
      await updateUserOffice(userEmail, code, checked, token);
      onOfficeChange && onOfficeChange();
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (officeCode) => {
    setExpanded((prev) => ({
      ...prev,
      [officeCode]: !prev[officeCode],
    }));
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* === Trigger Button === */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        title={open ? "Close Office Manager" : "Add / Manage Offices"}
        className={`flex items-center justify-center w-7 h-7 rounded-md border border-gray-300 
          text-[#1B7F48] bg-white transition-all shadow-sm hover:shadow-md
          ${open ? "bg-[#eafaf1] border-[#1B7F48]/50" : "hover:bg-[#f4fbf7]"}`}
      >
        {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </button>

      {/* === Dropdown === */}
      {open && (
        <div
          className="absolute right-0 mt-2 w-[340px] bg-white border border-gray-200 
                     rounded-md shadow-xl z-10 overflow-y-auto 
                     max-h-[calc(100vh-10rem)] scrollbar-thin scrollbar-thumb-gray-300"
        >
          {loading ? (
            <div className="flex items-center justify-center py-4 gap-2 text-gray-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading offices...
            </div>
          ) : allOffices.length === 0 ? (
            <div className="text-gray-500 text-center text-xs py-4">
              No offices found
            </div>
          ) : (
            allOffices.map((office) => {
              const isChecked = userOfficeCodes.includes(office.officeCode);
              const loc = office.location;
              const isExpanded = expanded[office.officeCode];

              return (
                <div
                  key={office.officeCode}
                  className="border-b border-gray-100 last:border-none px-3 py-2 hover:bg-[#f9fffa]"
                >
                  {/* === Office Header === */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) =>
                          handleCheckbox(office.officeCode, e.target.checked)
                        }
                        className="accent-[#1B7F48] w-4 h-4 cursor-pointer"
                      />
                     <div className="flex flex-col leading-tight">
                    <span className="text-md font-semibold text-gray-800 whitespace-normal break-words">
                        {office.name || loc?.locationName || "Unnamed Office"}
                    </span>
                    <span className="text-[11px] text-gray-500 whitespace-normal break-words">
                        {loc
                        ? [loc.city, loc.state, loc.country].filter(Boolean).join(", ")
                        : "No location info"}
                    </span>
                    </div>

                    </div>

                    {/* === Expand / Collapse === */}
                    <button
                      onClick={() => toggleExpand(office.officeCode)}
                      className="text-gray-500 hover:text-[#1B7F48] transition"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* === Expanded Info === */}
                  {isExpanded && loc && (
                    <div className="mt-2 ml-6 border-l border-gray-200 pl-3 text-[11.5px] text-gray-700 flex flex-col gap-[0.25rem]">
                      <div className="flex items-start gap-1">
                        <MapPin className="w-3.5 h-3.5 mt-[1px] text-[#1B7F48]" />
                        <span>
                          {loc.addressLine1 || "No address available"}
                        </span>
                      </div>
                      <div>
                        ZIP: <span className="font-medium">{loc.zipCode || "—"}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default OfficeDropdown;
